/* eslint-disable */

import { isEmpty, noop } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { ImageWithFallback, PreviewPhoto } from '@sm360/widget-commons-basic-components';
import { PriceAmounts } from '@sm360/widget-commons-general';
import { LocaleService, ContentZonesService } from '@sm360/widget-commons-core-services';
import { TileDisclaimer } from '@sm360/widget-commons-tiles';
import { CurrencyFormats, Vehicle, ModelYear, VehicleConstants } from '@sm360/widget-commons-vehicle';

import config from '../../../config/index.js';

import Specification from './Specification/Specification.jsx';
import LoadingTile from './InventoryNewLoadingTile.jsx';
import './InventoryNewTile.scss';

const { PaymentDetail, TileTag, URL, VehicleModelV25 } = Vehicle;

const getCZDisclaimer = () => {
    return ContentZonesService.getContentZone('widget.listing.used', 'legal.disclaimer.new')
        ? ContentZonesService.getContentZone('widget.listing.used', 'legal.disclaimer.new').getValue()
        : '';
};

// Get content zone value for Custom tag (Custom tag set from XMS)
const getCZCustomTagContent = () => {
    return ContentZonesService.getContentZone('widget.listing.used', 'custom.tag.content')
        ? ContentZonesService.getContentZone('widget.listing.used', 'custom.tag.content').getValue()
        : '';
};

// Display new inventory catalog image or dealer uploaded image
const displayCatalogImg = () => {
    return ContentZonesService.getContentZone('widget.listing.used', 'show.catalog.img')
        ? ContentZonesService.getContentZone('widget.listing.used', 'show.catalog.img').getValue()
        : '';
};

const InventoryNewTile = ({
    onClick,
    vehicle,
    isLoading,
    priceLoading,
    defaultPicture,
    displayCreditRate,
    motoInsightNewEnabled,
    roadsterNewEnabled,
    eshopNewEnabled,
    shopClickDriveNewEnabled,
    roadsterLink,
    dealerCode,
    dealerBAC,
}) => {
    if (isLoading) return <LoadingTile />

    const lang = LocaleService.getSimpleLocale();
    const isComingSoon = vehicle?.comingSoon;

    const isInTransit = vehicle?.stickerStatusInTransit;

    const isOnOrder = vehicle?.stickerStatusOnOrder;

    let vehicleStatusFilterContent = '';
    if (isComingSoon) vehicleStatusFilterContent = LocaleService.t('VEHICLE_STATUS.COMING_SOON');
    if (isInTransit) vehicleStatusFilterContent = LocaleService.t('VEHICLE_STATUS.IN_TRANSIT');
    if (isOnOrder) vehicleStatusFilterContent = LocaleService.t('VEHICLE_STATUS.ON_ORDER');
    const vehicleCustomeTagContent =  vehicle?.custom && getCZCustomTagContent();

    const isSold = vehicle.isSold();

    const isVehicleStatusFilter = !isSold && (isComingSoon || isInTransit || isOnOrder);

    const selectedPurchaseMethodAmounts = vehicle.purchaseMethods.getSelectedPurchaseMethodPaymentOption();
    const totalRebates = selectedPurchaseMethodAmounts?.incentives?.total;

    const taxCodes = vehicle.purchaseMethods.getTaxCodesString()
        ? vehicle.purchaseMethods.getTaxCodesString()
        : LocaleService.t('taxes.taxes');

    const save = totalRebates > 0 && (
        <div role="button" tabIndex={0} className="listing-tile-car-save">
            <icon className="icon-promo2 is-price" />
            <span>
                {LocaleService.t('MAX_DISCOUNT_BEFORE')} <PriceAmounts price={LocaleService.l(totalRebates, CurrencyFormats.CURRENCY_0)} /> {LocaleService.t('MAX_DISCOUNT_AFTER')}
            </span>
        </div>
    );

    const vehicleElectricRebate = selectedPurchaseMethodAmounts?.incentives?.electricRebateIncluded;
    const electricRebate = vehicleElectricRebate && (
        <div className="listing-tile-car-save electricRebate">
            <icon className="icon-electric-vehicle" />
            <span>{LocaleService.t('VEHICLE_STATUS.ELECTRIC_REBATE')}</span>
        </div>
    );

    const driveTrain = !isEmpty(vehicle.driveTrain) && <p>{vehicle.driveTrain.name}</p>;
    const transmission = !isEmpty(vehicle.transmission) && <p>{vehicle.transmission.name}</p>;
    const engine = !isEmpty(vehicle.engine) && <p>{vehicle.engine.name}</p>;
    const tagline = !isEmpty(vehicle.tagline) && <p>{vehicle.tagline}</p>;

    const stockNumber = !!vehicle.stockNumber && (
        <p className="sr-disclaimer listing-tile-specification-stock">
            {LocaleService.t('SUMMARY.STOCK_NUMBER', { stock: vehicle.stockNumber })}
        </p>
    );

    const vinNo = !!vehicle.vin && <p>{LocaleService.t('SUMMARY.VIN_NUMBER', { vin: vehicle.vin })}</p>;

    const hasConfigurationToken = !!(vehicle && vehicle.configurationToken);

    const odometer =
        (vehicle.odometer &&
            vehicle.odometer > 0 &&
            vehicle?.vehicleConditionType === VehicleConstants.CONDITIONS.DEMO && (
                <div className="sr-disclaimer listing-tile-specification-odometer">{vehicle.odometer} km</div>
            )) ||
        '';

    // To check if its image is from new inventory or dealer uploaded image
    const isNewInventoryImage = displayCatalogImg();
    
    // set demo tag class based on inventory image and reserved tag availability
    let demoTagClass = 'demo-tag';
    if (isNewInventoryImage === 'false' && vehicle.multimedia.getMainImage360x270()) {
        if (vehicle.onHold) {
            demoTagClass = `${demoTagClass} with-reserve-tag`;
        }
    }
 
    const demoTag = vehicle?.demo && (
            <div className={demoTagClass}>{LocaleService.t('VEHICLE_STATUS.DEMO_TAG')}</div>
        );

    const shouldDisplayOrigin = ContentZonesService.getContentZone('widget.listing.used', 'should.display.origin')
        ? ContentZonesService.getContentZone('widget.listing.used', 'should.display.origin').getValue() === 'true'
        : false;

    const roadsterCZCtaLabel =
        ContentZonesService.getContentZone('widget.listing.used', 'roadster.cta.label.new')
            ? ContentZonesService.getContentZone('widget.listing.used', 'roadster.cta.label.new').getValue()
            : LocaleService.t('CALL_TO_ACTION.BTN_ROADSTER_TEXT');

    const shopClickDriveCZCtaLabel =
        ContentZonesService.getContentZone('widget.listing.used', 'shop.click.drive.cta.label.new')
            ? ContentZonesService.getContentZone('widget.listing.used', 'shop.click.drive.cta.label.new').getValue()
            : LocaleService.t('CALL_TO_ACTION.BTN_SHOP_CLICK_DRIVE_TEXT');

    const seeDetailsCZCtaLabel = ContentZonesService.getContentZone('widget.listing.used', 'see.details.cta.label.new')
        ? ContentZonesService.getContentZone('widget.listing.used', 'see.details.cta.label.new').getValue()
        : LocaleService.t('CALL_TO_ACTION.BTN_SEE_DETAILS_TEXT');

    const vehicleImage = vehicle.multimedia?.getMainImage360x270() || defaultPicture;
    const customTag = (<TileTag
        shouldRender={vehicleCustomeTagContent}
        background="#f2bf24"
        color="#fff"
        text={vehicleCustomeTagContent}
        extraClass="custom-tag"
    />);

    const displayCompare = ContentZonesService.getContentZone(
        'widget.listing.used',
        'widget.listing.used.show.compare.new'
    );

    return (
        <div
            id={vehicle.vehicleId}
            tabIndex={0}
            className="listing-new-tile"
            onClick={hasConfigurationToken ? onClick : ''}
        >
            <div className="listing-new-tile-image">
                <div className="listing-new-rebate">
                {isNewInventoryImage !== 'false' && (   
                    <div className='top-tile-tag-label'>
                        {demoTag}
                        {customTag}
                     </div>
                )}
                    {save}
                    {electricRebate}
                </div>
                <div className="new-car-image-wrapper" role="button">
                    {!isLoading && (
                        <ImageWithFallback
                            alt={vehicle.getYearMakeModel()}
                            className="car-image"
                            imageUrl={
                                isNewInventoryImage === 'false'
                                    ? vehicle.multimedia.getMainImage360x270()
                                    : vehicle.multimedia.getCatalogSideImage360x270() // catalog side image for new inventory
                            }
                            // Display catalog image as fallback, if catalog image is not available then display the no photo image
                            fallbackImageUrl={vehicle.multimedia.getCatalogSideImage360x270() || `${config.source.image}/ir/w360h197c/images/${URL.NO_PHOTO}`}
                        />
                    )}
                    <TileTag
                        shouldRender={!isSold && vehicle.onHold}
                        icon="icon-lock"
                        background="#F5273F"
                        color="#fff"
                        text={LocaleService.t('VEHICLE_STATUS.RESERVED')}
                        dealerClass={
                            (isNewInventoryImage === 'false' &&
                            vehicle.multimedia.getMainImage360x270() &&
                            'tile-tag-with-dealerImg') || ''
                        }
                    />
                    <TileTag
                        shouldRender={isVehicleStatusFilter}
                        background="#000"
                        color="#fff"
                        text={vehicleStatusFilterContent}
                    />
                    <TileTag
                        shouldRender={isSold}
                        icon="icon-lock"
                        background="#F5273F"
                        color="#fff"
                        text={LocaleService.t('VEHICLE_STATUS.SOLD')}
                    />
                    {isNewInventoryImage === 'false' && (
                        <div className='top-tile-tag'>
                            {demoTag}
                            {customTag}
                        </div>
                    )}
                    {vehicle.exteriorColourStatus < ModelYear.EXTERIOR_COLOUR_STATUSES.FULL_COLORIZED &&
                        vehicle.exteriorColourStatus > 0 && (
                            <p className="vehicle-image-status is-light">
                                {LocaleService.t('MISSING_PHOTO.REFERENCE_PHOTO')}
                            </p>
                        )}
                </div>
            </div>

            <div
                className={`listing-new-tile-wrapper ${displayCatalogImg() === 'false' &&
                    vehicle.multimedia.getMainImage360x270() &&
                    'dealer-image'}`}
            >

                <div className="listing-new-tile-drivePowerTrains">

                    <div className="new-car-name sr-text is-bold">
                        {vehicle.getYearModel()} <span className="sr-disclaimer">{vehicle.modelTrim?.name}</span>
                    </div>
                    <div className="new-car-motor">
                        {driveTrain}
                        {transmission}
                        {engine}
                    </div>
                    <div className="new-car-tagline">
                        {tagline}
                    </div>
                </div>

                <div className="listing-tiles-payment">
                    <PaymentDetail
                        purchaseMethods={vehicle.purchaseMethods}
                        taxCodes={taxCodes}
                        isUsed={false}
                        priceLoading={priceLoading}
                        displayCreditRate={displayCreditRate}
                        displayThirdPartyCtas={true}
                        onClick={onClick}
                        motoInsightEnabled={motoInsightNewEnabled}
                        roadsterEnabled={roadsterNewEnabled}
                        shopClickDriveEnabled={shopClickDriveNewEnabled}
                        eshopEnabled={eshopNewEnabled}
                        roadsterLink={roadsterLink}
                        ddealerBAC={dealerBAC}
                        dealerCode={dealerCode}
                        vehicle={vehicle}
                        roadsterCZCtaLabel={roadsterCZCtaLabel}
                        shopClickDriveCZCtaLabel={shopClickDriveCZCtaLabel}
                        hasConfigurationToken={hasConfigurationToken}
                        seeDetailsCZCtaLabel={seeDetailsCZCtaLabel}

                    />
                </div>
                {displayCompare &&
                    displayCompare.getValue() === 'true' && (
                        <Vehicle.CompareButton
                            categoryName="new"
                            vehicleId={vehicle.vehicleId}
                        />
                    )}
                <TileDisclaimer
                    uniqueDisclaimer={vehicle.purchaseMethods.disclaimer}
                    czDisclaimer={getCZDisclaimer()}
                    currentFinanceType={vehicle.purchaseMethods.getSelectedPurchaseMethodValue()}
                />
                <div className="listing-tile-vin">{vinNo}</div>
                <div className="listing-new-tile-spec">
                    <Specification vehicle={vehicle} />
                    <div className="listing-tile-specification-specifics">
                        {stockNumber}
                        {odometer}
                        <PreviewPhoto
                            imageUrl={vehicleImage}
                            noPhotoUrl={URL.NO_PHOTO}
                            pictures={vehicle.multimedia.pictures}
                            yearMakeModel={vehicle.getYearMakeModel()}
                        />
                    </div>
                </div>

                <div className="listing-tile-footer">
                    {shouldDisplayOrigin &&
                        vehicle.primaryOrganizationUnit?.name && (
                            <div className="listing-tile-origin is-light">
                                <icon className="icon-map" />
                                <p>
                                    {LocaleService.t('VEHICLE_TILE.ORIGIN', {
                                        organizationUnitName: vehicle.primaryOrganizationUnit.name,
                                    })}
                                </p>
                            </div>
                        )}
                </div>
            </div>
        </div>
    );
};
InventoryNewTile.defaultProps = {
    onClick: noop,
    defaultPicture: '',
    displayCreditRate: false,
    motoInsightNewEnabled: false,
    roadsterNewEnabled: false,
    eshopNewEnabled: false,
    shopClickDriveNewEnabled: false,
    roadsterLink: '',
    dealerCode: '',
    dealerBAC: '',
};

InventoryNewTile.propTypes = {
    onClick: PropTypes.func,
    vehicle: PropTypes.instanceOf(VehicleModelV25).isRequired,
    isLoading: PropTypes.bool.isRequired,
    defaultPicture: PropTypes.string,
    displayCreditRate: PropTypes.bool,
    motoInsightNewEnabled: PropTypes.bool,
    roadsterNewEnabled: PropTypes.bool,
    eshopNewEnabled: PropTypes.bool,
    shopClickDriveNewEnabled: PropTypes.bool,
    roadsterLink: PropTypes.string,
    dealerCode: PropTypes.string,
    dealerBAC: PropTypes.string,
};

InventoryNewTile.displayName = 'Tile-A.InventoryNewTile';

export default InventoryNewTile;
